import React, {useMemo, useState} from 'react';

import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { Grid, Typography } from '@material-ui/core';
import { GridGrow } from '../../components/GridGrow';
import { VerticalCenter } from '../../components/VerticalCenter';
import { useIsMobile } from '../../components/Mui3WidthProvider';
import { RightSideInteractions } from './LibraryAssetActions/RightSideInteractions';
import { RightDrawer } from './RightDrawer/RightDrawer';
import { useLibrary } from './LibraryPage';
import { HorizontalCentered } from '../../components/HorizontalCentered';
import { DesktopRecentTable } from './Views/Components/DesktopRecentTable';
import { orderBy } from '../../utilities/orderBy';
import { MobileLibraryAssetTable } from './Views/Components/MobileLibraryAssetTable';
import { PageTitle } from '../../components/PageTitle';

interface Props {
}

const startOfWeekFind = new Date();
const day = startOfWeekFind.getDay();
const diff = startOfWeekFind.getDate() - day;
const startOfMonthFind = new Date();

const midNightToday = new Date(new Date().setHours(0, 0, 0, 0));
const firstDayOfWeekMidnight = new Date(new Date(startOfWeekFind.setDate(diff)).setHours(0, 0, 0, 0));
const firstDayOfMonth = new Date(startOfMonthFind.getFullYear(), startOfMonthFind.getMonth(), 1);
const firstDayOfYear = new Date(new Date().getFullYear(), 0, 1);

export function LibraryRecent(props:Props) {
    const isMobile = useIsMobile();

    const context = useLibrary();
    const { libraryItems } = context;

    const itemsAccessedToday = useMemo(() => 
        libraryItems
        .filter(i => i.dateModified > midNightToday && i.dateModified <= new Date() )
        .sort(orderBy.date(i => i.dateModified, "Descending"))
    , [libraryItems]);

    const itemsAccessedThisWeek = useMemo(() => 
        libraryItems
        .filter(i => i.dateModified > firstDayOfWeekMidnight && i.dateModified <= midNightToday)
        .sort(orderBy.date(i => i.dateModified, "Descending"))
    , [libraryItems]);

    const itemsAccessedThisMonth = useMemo(() => 
        libraryItems
        .filter(i => i.dateModified > firstDayOfMonth && i.dateModified <= firstDayOfWeekMidnight)
        .sort(orderBy.date(i => i.dateModified, "Descending"))
    , [libraryItems]);

    const itemsAccessedThisYear = useMemo(() => 
        libraryItems
        .filter(i => i.dateModified > firstDayOfYear && i.dateModified <= firstDayOfMonth)
        .sort(orderBy.date(i => i.dateModified, "Descending"))
    , [libraryItems]);

    const [focusedItemId, setFocusedItemId] = useState<string>();
    const focusedItem = useMemo(() => libraryItems.find(i => i.id === focusedItemId), [focusedItemId, libraryItems])
    const hasItems = useMemo(() => 
        itemsAccessedToday.length !== 0 || itemsAccessedThisWeek.length !== 0 || itemsAccessedThisMonth.length !== 0 || itemsAccessedThisYear.length !== 0
    , [itemsAccessedThisYear, itemsAccessedThisMonth, itemsAccessedThisWeek, itemsAccessedToday])

    if(isMobile)
        return (
            <MobileLibraryAssetTable 
                items={libraryItems}
                focusedItem={focusedItem}
                setFocusedItemId={(id) => setFocusedItemId(id)}
            />
        )

    return (
        <>
        <PageTitle title="Sermon Maker - Recent"/>
        { isMobile
        ?
            <MobileLibraryAssetTable 
                items={libraryItems}
                focusedItem={focusedItem}
                setFocusedItemId={(id) => setFocusedItemId(id)}
            />
        :
            <div style={{height: "100%"}}>
                <Grid container style={{height: 45, borderBottom: "1px solid rgb(225, 225, 225)"}}>
                    <Grid container item md={8} lg={9}  alignItems="center">
                        <Grid item>
                            <Typography style={{ color: "#5f6368", fontSize: "18px", fontWeight: 500, fontFamily: "Roboto, Helvetica"}}>
                                Recent
                            </Typography>
                        </Grid>
                        <GridGrow />
                    </Grid>
                    <Grid container item md={4} lg={3} >
                        <GridGrow />
                        <Grid item>
                            <RightSideInteractions focusedItem={focusedItem} />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid container style={{height: "calc(100% - 45px)"}}>
                    <Grid item md={8} lg={9} style={{height: "100%", overflowY: "auto"}}>
                    {!hasItems
                    ?
                        <VerticalCenter>
                            <HorizontalCentered>
                                <HorizontalCentered>
                                    <AccessTimeIcon style={{ width: "150px", height: "150px", color: "#CCC"}}/>
                                </HorizontalCentered>
                                <Typography 
                                    style={{ 
                                        fontFamily: "Roboto, Helvetica", fontSize: "12px", marginTop: "12px", color: "#5f6368"
                                    }}
                                    >No recent items to display</Typography>
                            </HorizontalCentered>
                        </VerticalCenter>
                    :
                        <div>
                            {itemsAccessedToday.length > 0
                                &&
                            <DesktopRecentTable 
                                setFocusedItemId={(i) => setFocusedItemId(i)} 
                                items={itemsAccessedToday}
                                title={'Today'}
                                showTimeString={true} 
                                focusedItemId={focusedItemId}
                            />
                            }
                            {itemsAccessedThisWeek.length > 0
                                &&
                            <DesktopRecentTable 
                                setFocusedItemId={(i) => setFocusedItemId(i)} 
                                items={itemsAccessedThisWeek}
                                title={'Earlier this week'}
                                showTimeString={false} 
                                focusedItemId={focusedItemId}
                            />
                            }
                            {itemsAccessedThisMonth.length > 0
                                &&
                            <DesktopRecentTable 
                                setFocusedItemId={(i) => setFocusedItemId(i)} 
                                items={itemsAccessedThisMonth}
                                title={'Earlier this month'}
                                showTimeString={false} 
                                focusedItemId={focusedItemId}
                            />
                            }
                            {itemsAccessedThisYear.length > 0
                                &&
                            <DesktopRecentTable 
                                setFocusedItemId={(i) => setFocusedItemId(i)} 
                                items={itemsAccessedThisYear}
                                title={'Earlier this year'}
                                showTimeString={false} 
                                focusedItemId={focusedItemId}
                            />
                            }
                        </div>
                    }
                    </Grid>
                    <Grid item md={4} lg={3} style={{height: "100%", borderLeft: "1px solid rgb(225, 225, 225)"}}>
                        <RightDrawer item={focusedItem} />
                    </Grid>
                </Grid>
            </div>
        }
        </>
    );
}